'use client';

/**
 * Team Member Card Component
 * Displays a team member with their meeting history
 */

import { useMemo } from 'react';
import { TeamMember, MeetingWithMember } from '@/lib/one-on-one/types';

interface TeamMemberCardProps {
  member: TeamMember;
  lastMeeting?: MeetingWithMember;
  pendingActions?: number;
  onClick: () => void;
  onScheduleMeeting: () => void;
}

export function TeamMemberCard({
  member,
  lastMeeting,
  pendingActions = 0,
  onClick,
  onScheduleMeeting,
}: TeamMemberCardProps) {
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  };

  // Calculate days since last meeting
  const daysSinceLastMeeting = useMemo(() => {
    if (!lastMeeting?.completed_at) return null;
    const now = new Date();
    const completedDate = new Date(lastMeeting.completed_at);
    return Math.floor((now.getTime() - completedDate.getTime()) / (1000 * 60 * 60 * 24));
  }, [lastMeeting]);

  return (
    <div
      onClick={onClick}
      className="border border-border rounded-lg p-4 hover:border-border-muted0 transition-colors cursor-pointer"
    >
      <div className="flex items-start gap-3">
        {/* Avatar */}
        {member.avatar_url ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={member.avatar_url}
            alt={member.name}
            className="w-12 h-12 rounded-full"
          />
        ) : (
          <div className="w-12 h-12 rounded-full bg-accent-muted flex items-center justify-center text-accent font-semibold">
            {getInitials(member.name)}
          </div>
        )}

        {/* Info */}
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-foreground truncate">{member.name}</h3>
          {member.metadata.role && (
            <p className="text-sm text-foreground-tertiary truncate">{member.metadata.role}</p>
          )}

          {/* Stats */}
          <div className="flex items-center gap-4 mt-2 text-xs text-foreground-tertiary">
            {lastMeeting ? (
              <span>
                Last: {formatDate(lastMeeting.completed_at || lastMeeting.created_at)}
                {daysSinceLastMeeting !== null && daysSinceLastMeeting > 14 && (
                  <span className="text-accent ml-1">({daysSinceLastMeeting}d ago)</span>
                )}
              </span>
            ) : (
              <span>No meetings yet</span>
            )}

            {pendingActions > 0 && (
              <span className="text-accent">
                {pendingActions} pending action{pendingActions !== 1 ? 's' : ''}
              </span>
            )}
          </div>
        </div>

        {/* Quick action */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onScheduleMeeting();
          }}
          className="p-2 text-foreground-tertiary hover:text-accent transition-colors"
          title="Schedule meeting"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        </button>
      </div>
    </div>
  );
}

export default TeamMemberCard;
